import {
    Button,
    Form,
    Input,
    Select,
    Space,
    Modal,
    Table,
    InputNumber,
    Tooltip,
    Switch,
    Avatar,
    message,
    Checkbox,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
import BaseModal from '&/components/BaseModal';
import ShareCard from '&/components/ShareCard';
import {
    memberList,
    memberEdit,
    deleteMember,
    inviteLink,
    postHandover,
    getHandover,
} from '@/api/personCenter/organization';
import { getRoleList } from '@/api/common';
import { logoQRcodeGenerate } from '&/utils/commonForPC';
import quickStartGuide from '&/store/quickStartGuide';
import './index.scss';
import { useEventEmitter } from '&/utils/customHooks/useEventEmitter';
import useStoreState from '&/utils/customHooks/useStoreState';
import warningIcon from '&/assets/img/userCenter/warnimg.png';

export default function OrganizationMemberList() {
    const [form] = Form.useForm();
    const [, forceUpdate] = useState({}); // To disable submit button at the beginning.
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState([]);
    const { Item } = Form;
    const [modalVisible, setModalVisible] = useState(false);
    const [selectRole, setSelectRole] = useState();
    const [limitNum, setLimitNum] = useState(1);
    const [loadUrl, setLoadUrl] = useState(false);
    const [inviteData, setInviteData] = useState({});
    const { useListener, emit } = useEventEmitter();
    const [addNumberForm] = Form.useForm();
    const [addNumberDisable, setAddNumberDisable] = useState(true);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editNumberForm] = Form.useForm();
    const [entInfo, setEntInfo] = useState({});
    const [organization, setOrganization] = useState([]);
    const [depList, setDepList] = useState([]);
    const [roleList, setRoleList] = useState([{ id: '', name: '全部' }]);
    const [title, setTitle] = useState('');
    const [editRowData, setEditRowData] = useState({});
    const appStore = useStoreState('appStore');
    const { organization: info } = appStore;

    useListener('selected-node', ([node, treeData]) => {
        setEntInfo(node);
        if (Object.keys(node).length === 0) return;
        getRoleList({
            entId: !node.children ? node.enterpriseId : node.id,
            isProject: 2,
        })
            .then(res => {
                let list = [...res.data];
                if (!list.find(v => v.id === selectRole)) {
                    setSelectRole(null);
                }
                setRoleList(() => [{ id: '', name: '全部' }, ...list]);
            })
            .finally(() => {});
        if (!node.children) {
            setAddNumberDisable(false);
        } else {
            setAddNumberDisable(true);
        }
    });

    useListener('organization', ([treeData]) => {
        setOrganization(treeData);
    });

    const asyncOrganization = () => {
        if (!entInfo.children) {
            setTitle(
                getOrganizationByDep(entInfo)?.value + '/' + entInfo.value
            );
        } else {
            setTitle(entInfo.value);
        }
    };

    useEffect(() => {
        if (Object.keys(entInfo).length > 0) {
            queryList();
            asyncOrganization();
        }
    }, [entInfo]);

    useEffect(() => {
        asyncOrganization();
    }, [organization]);

    const onCloseAddModal = () => {
        setModalVisible(false);
    };

    async function onAdd() {
        onCloseAddModal();
    }

    function addTurbine() {
        setModalVisible(true);
        addNumberForm?.setFieldsValue({
            organization: getOrganizationByDep(entInfo)?.value,
            department: entInfo?.value,
            authCloud: false,
            authDesign: false,
        });
    }

    const onRoleChange = val => {
        setSelectRole(val);
    };

    const getShareUrl = async () => {
        if (
            !limitNum ||
            (result.some(e => e.roleId === '7') && selectRole === '7')
        )
            return;

        setLoadUrl(true);
        try {
            const { data } = await inviteLink({
                role: selectRole,
                entId: entInfo.enterpriseId,
                depId: entInfo.id,
                limit: limitNum,
                authCloud: addNumberForm.getFieldValue('authCloud') ? 0 : 1,
                authDesign: addNumberForm.getFieldValue('authDesign') ? 0 : 1,
            });

            const queryString = {
                invitation: data.url.invitation,
                invUserName: data.url.invUserName,
                enterpriseName: data.url.enterprise.enterpriseName,
                scene: 1,
                department: addNumberForm.getFieldValue('department'),
                role: roleList.find(v => v.id === selectRole).name,
            };
            const url = `${
                data.url.domain.m
            }/b/pages/homePage/InviteMembers?${new URLSearchParams(
                queryString
            ).toString()}`;
            const code = await logoQRcodeGenerate({ url });
            setInviteData({
                url,
                code,
                month: data.url.validDay?.month,
                day: data.url.validDay?.day,
                queryString,
            });
        } finally {
            setLoadUrl(false);
        }
    };

    // 是否带上搜索条件
    const queryList = (needCondition = true) => {
        let searchInfo = form.getFieldsValue();
        let params = {};
        if (!entInfo.children) {
            params = {
                entId: entInfo.enterpriseId,
                depId: entInfo.id,
            };
        } else {
            setAddNumberDisable(true);
            params = { entId: entInfo.id };
        }

        if (needCondition) {
            params = Object.assign(params, searchInfo);
            setIsLoading(true);
        } else {
            params = { entId: entInfo.enterpriseId || entInfo.id, status: 0 };
        }

        memberList(params)
            .then(({ data }) => {
                if (needCondition) return setResult(data);
                const transferUserList = [];
                for (const el of data) {
                    if (el.userId !== currentUserId.current) {
                        transferUserList.push({
                            value: el.userId,
                            label: el.username,
                        });
                    }
                }
                setUserList(transferUserList);
            })
            .finally(() => {
                needCondition && setIsLoading(false);
            });
    };

    useEffect(() => {
        forceUpdate({});
    }, []);

    useEffect(() => {
        modalVisible && selectRole && getShareUrl();
    }, [modalVisible, selectRole]);

    const openForm = row => {
        editNumberForm.setFieldsValue({
            ...row,
            role: row.roleId,
            department: row.departmentId,
            authCloud: row.authCloud === '0',
            authDesign: row.authDesign === '0',
        });
        setEditRowData(row);
        setDepList(getOrganizationByDep({ id: row.departmentId })?.department);
        setEditModalVisible(true);
    };

    const hideEditModal = () => {
        setEditRowData({});
        setEditModalVisible(false);
    };

    const isCurrentUser = () => {
        try {
            if (editRowData.id) {
                return appStore.userInfo.userId === editRowData.userId;
            }
            return false;
        } catch {}
    };

    const checkedRef2 = useRef(false);
    const onCheckboxChange2 = e => {
        checkedRef2.current = e.target.checked;
    };
    const [neverTip2, setNeverTip2] = useState(
        +localStorage.getItem('never_tip2')
    );

    const onDelete = row => {
        const delMember = async () => {
            await deleteMember({
                memberId: row.id,
                entId: row.enterpriseId,
                depId: row.departmentId,
            });
            message.success('账号删除成功');
            queryList();
        };

        if (neverTip2 === 1) {
            delMember();
            return;
        }

        const modal = Modal.confirm({
            okText: intl.get('base.delete'),
            cancelText: intl.get('base.cancel'),
            okButtonProps: { loading: false },
            width: 300,
            className: 'confirm-modal',
            title: '账号删除后将无法还原',
            icon: (
                <img src={warningIcon} alt="warning" className="confirm-icon" />
            ),
            content: <Checkbox onChange={onCheckboxChange2}>不再提示</Checkbox>,
            centered: true,
            onOk: async () => {
                modal.update({ okButtonProps: { loading: true } });
                if (checkedRef2.current) {
                    localStorage.setItem('never_tip2', 1);
                    setNeverTip2(1);
                }
                try {
                    await delMember();
                } finally {
                    modal.update({ okButtonProps: { loading: false } });
                }
            },
        });
    };

    const onEditConfirm = () => {
        editNumberForm.submit();
    };

    const onEditFinish = values => {
        let params = {
            entId: values.enterpriseId,
            depId: values.department,
            memberId: values.id,
            role: values.role,
            authCloud: values.authCloud ? 0 : 1,
            authDesign: values.authDesign ? 0 : 1,
        };
        memberEdit(params)
            .then(() => {
                queryList();
                appStore.queryUserInfo();
                emit('update-tree-data');
            })
            .finally(() => {
                hideEditModal();
            });
    };

    const [quittedCurrent, setQuittedCurrent] = useState({});
    const [isOpen, setIsOpen] = useState(false);

    const schemeRow = {
        id: 0,
        name: '方案',
        desc: '我的云方案数据、协作方案数据、平面图方案数据',
        way: (
            <Item name="dop" noStyle initialValue={1}>
                <AntSelect
                    bordered={false}
                    options={[
                        {
                            value: 1,
                            label: '转移给其他成员',
                        },
                        {
                            value: 2,
                            label: (
                                <>
                                    直接删除
                                    <Tooltip
                                        title={
                                            <>
                                                <p>
                                                    ·
                                                    删除该成员创建的所有云方案数据（公开至团队的方案不受影响）
                                                </p>
                                                <p>
                                                    ·
                                                    删除该成员发起的所有协作方案数据（参与的协作方案已评论数据不受影响）
                                                </p>
                                            </>
                                        }>
                                        <i className="filled icon-info-circle2"></i>
                                    </Tooltip>
                                </>
                            ),
                        },
                    ]}></AntSelect>
            </Item>
        ),
    };
    const projectRow = {
        id: 1,
        name: '项目',
        desc: '我的项目数据',
        way: '转移给其他成员',
    };

    const [quittedRows, setQuittedRows] = useState([]);

    useEffect(() => {
        if (!isOpen) return;
        setShowRecipient(true);
        handOverRef.resetFields();
    }, [isOpen]);

    const [showRecipien, setShowRecipient] = useState(true);

    const columnsQuitted = [
        {
            title: '资源',
            dataIndex: 'name',
            width: 56,
            align: 'center',
        },
        {
            title: '描述',
            dataIndex: 'desc',
        },
        {
            title: '移交方式',
            dataIndex: 'way',
        },
        {
            title: '接收人',
            dataIndex: 'id',
            render(id, row) {
                return !showRecipien && id === 0 ? null : (
                    <Item
                        name={
                            id === 0
                                ? 'schemeAcceptMemberId'
                                : id === 1
                                ? 'projectAcceptMemberId'
                                : 'planAcceptMemberId'
                        }
                        shouldUpdate
                        noStyle>
                        <Select
                            dropdownMatchSelectWidth={100}
                            suffixIcon={
                                <EditOutlined
                                    style={{
                                        fontSize: '14px',
                                        color: 'rgba(40, 85, 235, 1)',
                                        marginTop: '-2px',
                                    }}
                                    className="user-edit-icon"
                                />
                            }
                            bordered={false}
                            placeholder="请选择"
                            options={userList}></Select>
                    </Item>
                );
            },
        },
    ];

    const [handOverRef] = Form.useForm();

    const onClose = () => {
        setIsOpen(false);
    };

    const [userList, setUserList] = useState();
    const currentUserId = useRef();

    // 快捷离职
    const shortcut = user => {
        const modal = Modal.confirm({
            width: 300,
            className: 'confirm-modal',
            title: '确认当前成员已离职？',
            icon: (
                <img src={warningIcon} alt="warning" className="confirm-icon" />
            ),
            content: '离职后将无法还原',
            okButtonProps: { loading: false },
            onOk: async () => {
                modal.update({ okButtonProps: { loading: true } });
                try {
                    await postHandover({
                        entId: entInfo.enterpriseId || entInfo.id,
                        memberId: user.userId,
                    });
                    queryList();
                } catch {
                } finally {
                    modal.update({ okButtonProps: { loading: false } });
                }
            },
            centered: true,
        });
    };

    const openModal = () => {
        queryList(false);
        setIsOpen(true);
    };

    const checkedRef = useRef(false);
    const onCheckboxChange = e => {
        checkedRef.current = e.target.checked;
    };
    const [neverTip, setNeverTip] = useState(
        +localStorage.getItem('never_tip1')
    );

    const openQuittedOperation = async user => {
        setQuittedCurrent(user);
        currentUserId.current = user.userId;

        // 查看离职者账号下的方案项目数量
        getHandover({
            memberId: user.userId,
            entId: entInfo.enterpriseId || entInfo.id,
        })
            .then(({ data }) => {
                const rows = [];
                data.projects.length && rows.push(projectRow);
                (data.schemes.length || data.plans.length) &&
                    rows.push(schemeRow);
                setQuittedRows(rows);
                return Promise.resolve(
                    !data.projects.length &&
                        !data.schemes.length &&
                        !data.plans.length
                );
            })
            .then(isEmpty => {
                if (isEmpty) {
                    shortcut(user);
                } else {
                    neverTip === 1
                        ? openModal()
                        : Modal.confirm({
                              width: 300,
                              className: 'confirm-modal',
                              title: '成员资源移交后可离职',
                              icon: (
                                  <img
                                      src={warningIcon}
                                      alt="warning"
                                      className="confirm-icon"
                                  />
                              ),
                              content: (
                                  <Checkbox onChange={onCheckboxChange}>
                                      不再提示
                                  </Checkbox>
                              ),
                              onOk: () => {
                                  if (checkedRef.current) {
                                      localStorage.setItem('never_tip1', 1);
                                      setNeverTip(1);
                                  }
                                  openModal();
                                  return Promise.resolve();
                              },
                              centered: true,
                          });
                }
            });
    };

    const onFieldsChange = changedFields => {
        const { name, value } = changedFields[0];

        if (name[0] === 'dop') {
            if (value === 2) {
                handOverRef.setFieldsValue({ schemeAcceptMemberId: undefined });
                setShowRecipient(false);
            } else {
                setShowRecipient(true);
            }
        }
    };

    const [loading, setloading] = useState(false);

    const onSubmitQuitted = () => {
        checkedRef.current = false;
        let formData = handOverRef.getFieldsValue();
        if (
            (!!quittedRows.find(el => el.id === 0) &&
                !formData.schemeAcceptMemberId &&
                showRecipien) ||
            (!!quittedRows.find(el => el.id === 1) &&
                !formData.projectAcceptMemberId)
        ) {
            message.warn('请选择接收人');
            return;
        }
        setloading(true);
        formData.entId = entInfo.enterpriseId || entInfo.id;
        formData.memberId = quittedCurrent.userId;
        postHandover(formData)
            .then(() => {
                onClose();
                queryList();
            })
            .finally(() => setloading(false));
    };

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 80,
            render: (_, __, idx) => {
                return idx + 1;
            },
        },
        {
            title: intl.get('projectSpace.nickName'),
            dataIndex: 'username',
            ellipsis: true,
            render: role => role,
        },
        {
            title: intl.get('projectSpace.role'),
            dataIndex: 'roleName',
        },
        {
            title: () => {
                return (
                    <div className="rights-title-box">
                        {intl.get('userCenter.equityEnterprise')}
                        <Tooltip
                            placement="top"
                            overlayClassName="equity-enterprise-tip"
                            title={intl.getHTML('userCenter.rightsPrompt')}>
                            <i className="filled icon-question-cirlce2"></i>
                        </Tooltip>
                    </div>
                );
            },
            render: row => {
                let authStr = '';
                if (row.authCloud === '0') {
                    authStr += intl.get('userCenter.cloudServiceRights');
                }
                if (row.authCloud === '0' && row.authDesign === '0') {
                    authStr += ',';
                }
                if (row.authDesign === '0') {
                    authStr += `${intl.get('userCenter.designRights')}`;
                }

                return authStr;
            },
        },
        {
            title: '手机号',
            dataIndex: 'mobile',
            align: 'center',
            width: 140,
            render: mobile => mobile,
        },
        {
            title: '创建日期',
            dataIndex: 'createDate',
        },
        {
            title: '账号状态',
            align: 'center',
            dataIndex: 'status',
            render(val) {
                return statusList.find(el => el.value === +val)?.name;
            },
        },
        {
            title: intl.get('base.operation'),
            key: '7',
            render: row => {
                return (
                    row.roleId !== '8' &&
                    info.roleFlag === 'admin' && (
                        <div className="btn-group">
                            {+row.status === 0 ? (
                                <>
                                    <div
                                        className="control-btns"
                                        onClick={() => openForm(row)}>
                                        {intl.get('base.edit')}
                                    </div>
                                    <div
                                        className={`control-btns${
                                            [
                                                row.entCreatorId,
                                                appStore.userInfo.userId,
                                            ].includes(row.userId)
                                                ? ' type--disabled'
                                                : ''
                                        }`}
                                        onClick={() =>
                                            ![
                                                row.entCreatorId,
                                                appStore.userInfo.userId,
                                            ].includes(row.userId) &&
                                            openQuittedOperation(row)
                                        }>
                                        操作离职
                                    </div>
                                </>
                            ) : (
                                <div
                                    className="control-btns"
                                    onClick={() => onDelete(row)}>
                                    删除
                                </div>
                            )}
                        </div>
                    )
                );
            },
        },
    ];

    info?.example === 0 && columns.splice(4, 1); // 示例项目的组织去掉电话显示    0-是，1-否 ,

    const getEquityForm = formType => {
        return (
            <>
                <Item
                    label={intl.get('userCenter.memberEquity')}
                    className="member-equity"
                    name="memberEquity"
                    required>
                    <span />
                </Item>
                <Item
                    label={intl.get('userCenter.cloudServiceRights')}
                    className="swtich-form">
                    <div className="switch-text-box">
                        <Item
                            valuePropName="checked"
                            className="swtich-sub-form"
                            name="authCloud">
                            <Switch />
                        </Item>
                        {formType === 'add' && (
                            <p className="switch-tip">
                                <i className="filled icon-info-circle1" />
                                {intl.get('userCenter.cloudServiceRightsTip')}
                            </p>
                        )}
                    </div>
                </Item>

                {formType === 'edit' && (
                    <p className="switch-tip">
                        <i className="filled icon-info-circle1" />
                        {intl.get('userCenter.cloudServiceRightsTip')}
                    </p>
                )}
                <Item
                    label={intl.get('userCenter.designRights')}
                    className="swtich-form">
                    <div className="switch-text-box">
                        <Item
                            valuePropName="checked"
                            className="swtich-sub-form"
                            name="authDesign">
                            <Switch
                                disabled={
                                    !editNumberForm?.getFieldValue(
                                        'authDesign'
                                    ) &&
                                    Number(entInfo?.entVip?.total_num) <=
                                        Number(entInfo?.entVip?.surplus_num)
                                }
                            />
                        </Item>
                        <p className="red-color">
                            {intl.getHTML('userCenter.openedNum', {
                                total: entInfo?.entVip?.total_num,
                                num: entInfo?.entVip?.surplus_num,
                            })}
                        </p>
                        {formType === 'add' && (
                            <p className="switch-tip">
                                <i className="filled icon-info-circle1" />
                                {intl.get('userCenter.designRightsTip')}
                            </p>
                        )}
                    </div>
                </Item>
                {formType === 'edit' && (
                    <p className="switch-tip">
                        <i className="filled icon-info-circle1" />
                        {intl.get('userCenter.designRightsTip')}
                    </p>
                )}
            </>
        );
    };

    const isBranch = !!info.branchId; // 是否是分公司

    const changeQuickstart = () => {
        quickStartGuide.setGuidanceStatus('entOrg', 1);
    };

    const SHARE_MODEL = (
        <>
            <Item validateStatus={!limitNum ? 'error' : ' '} label="成员数量">
                <InputNumber
                    value={limitNum}
                    onChange={setLimitNum}
                    step={1}
                    min={1}
                    max={99}
                    onBlur={getShareUrl}
                    placeholder={'请输入邀请成员数'}
                />
                <span className="tip">
                    {limitNum
                        ? '设置邀请成员的数量，默认1'
                        : '请输入邀请的成员数量'}
                </span>
            </Item>
            {appStore.userInfo.vipEntType === 'vip_ent_unite' &&
                appStore.userInfo.vipEntExpireFlag !== 1 &&
                !isBranch &&
                getEquityForm('add')}
            <ShareCard
                loadUrl={loadUrl}
                inviteData={inviteData}
                limitNum={limitNum}
                copyLinkCallback={changeQuickstart}
                handleCopyImgCallBack={changeQuickstart}
            />
        </>
    );

    const getOrganizationByDep = dep => {
        return organization.find(v =>
            v.department.find(item => item.id === dep.id)
        );
    };

    const statusList = [
        {
            name: '全部',
            value: 2,
        },
        {
            name: '在职',
            value: 0,
        },
        {
            name: '已离职',
            value: 1,
        },
    ];

    return (
        <div className="organization-member-list">
            <div className="header">
                <div className="organization-title">{title}</div>
                <div className="btns">
                    {appStore.userInfo.vipEntType === 'vip_ent_unite' &&
                        appStore.userInfo.vipEntExpireFlag !== 1 && (
                            <p>
                                {intl.get('userCenter.designRightsAccount', {
                                    total: entInfo?.entVip?.total_num,
                                    num: entInfo?.entVip?.surplus_num,
                                })}
                            </p>
                        )}
                    {info.roleFlag === 'admin' && (
                        <Button
                            type="primary"
                            onClick={addTurbine}
                            disabled={addNumberDisable}>
                            添加成员
                        </Button>
                    )}
                </div>
            </div>
            <div className="organization-content">
                <Form
                    form={form}
                    name="member-filter"
                    className="member-filter"
                    layout="inline"
                    onFinish={queryList}>
                    <Item name="role" initialValue={''} label="角色">
                        <Select placeholder="" onChange={form.submit}>
                            {roleList.map(v => (
                                <Select.Option value={v.id} key={v.id}>
                                    {v.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Item>
                    <Item name="status" initialValue={2} label="账号状态">
                        <Select onChange={form.submit}>
                            {statusList.map((v, idx) => (
                                <Select.Option value={v.value} key={idx}>
                                    {v.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Item>
                    <Item name="keyWord" label="搜索">
                        <Input
                            placeholder="输入昵称或手机号"
                            onBlur={form.submit}
                            onPressEnter={form.submit}
                        />
                    </Item>
                </Form>
                <Table
                    className="normal-table"
                    loading={isLoading}
                    columns={columns}
                    dataSource={result}
                    size="small"
                    pagination={false}
                    rowKey="id"
                    bordered
                    rowClassName={function (record, rowIndex) {
                        console.log('record', record);
                        // 已离职
                        return +record.status === 1 && 'is--quitted';
                    }}
                />
            </div>
            <BaseModal
                width="390px"
                visible={editModalVisible}
                getContainer={false}
                className="edit-member-form"
                onCancel={hideEditModal}
                footer={
                    <Space>
                        <Button onClick={hideEditModal}>
                            {intl.get('base.cancel')}
                        </Button>
                        <Button type="primary" onClick={onEditConfirm}>
                            {intl.get('base.confirm')}
                        </Button>
                    </Space>
                }
                title={'编辑成员信息'}>
                <Form
                    labelCol={{ span: 4 }}
                    labelAlign="right"
                    form={editNumberForm}
                    onFinish={onEditFinish}
                    className="add-form">
                    <Item label="昵称">
                        {/* <Input placeholder="输入手机号" /> */}
                        <span>
                            {editModalVisible &&
                                editNumberForm?.getFieldValue('username')}
                        </span>
                    </Item>
                    <Item
                        name="department"
                        label="部门"
                        rules={[{ required: true }]}>
                        {isCurrentUser() ? (
                            <span>
                                {
                                    depList.find(
                                        item =>
                                            item.id ===
                                            editNumberForm?.getFieldValue(
                                                'department'
                                            )
                                    )?.title
                                }
                            </span>
                        ) : (
                            <Select placeholder="">
                                {depList.map(v => (
                                    <Select.Option value={v.id} key={v.id}>
                                        {v.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </Item>

                    <Item name="role" label="角色" rules={[{ required: true }]}>
                        {isCurrentUser() ? (
                            <span>
                                {
                                    roleList.find(
                                        item =>
                                            item.id ===
                                            editNumberForm?.getFieldValue(
                                                'role'
                                            )
                                    )?.name
                                }
                            </span>
                        ) : (
                            <Select placeholder="">
                                {roleList
                                    .filter(v => v.id !== -1)
                                    .map(v => (
                                        <Select.Option value={v.id} key={v.id}>
                                            {v.name}
                                        </Select.Option>
                                    ))}
                                {/* <Select.Option value="china">China</Select.Option>
                            <Select.Option value="usa">U.S.A</Select.Option> */}
                            </Select>
                        )}
                    </Item>
                    <Item label="手机号">
                        <span>
                            {editModalVisible &&
                                editNumberForm?.getFieldValue('mobile')}
                        </span>
                    </Item>
                    {appStore.userInfo.vipEntType === 'vip_ent_unite' &&
                        appStore.userInfo.vipEntExpireFlag !== 1 &&
                        !isBranch &&
                        getEquityForm('edit')}
                    <Item hidden name="enterpriseId">
                        <Input />
                    </Item>
                    <Item hidden name="id">
                        <Input />
                    </Item>
                </Form>
            </BaseModal>

            <BaseModal
                width="36.25rem"
                visible={modalVisible}
                getContainer={false}
                footer={
                    <Space>
                        <Button onClick={onCloseAddModal}>
                            {intl.get('base.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={onAdd}
                            disabled={!selectRole}>
                            {intl.get('base.confirm')}
                        </Button>
                    </Space>
                }
                className="add-member-dialog"
                title={intl.get('projectSpace.addMembers')}
                onCancel={onCloseAddModal}>
                <Form
                    labelCol={{ span: 4 }}
                    labelAlign="right"
                    form={addNumberForm}
                    onFieldsChange={getShareUrl}
                    className="add-form">
                    <section className="form-group">
                        {selectRole && (
                            <>
                                <Item label="分配组织">
                                    <Input
                                        placeholder=""
                                        disabled
                                        value={
                                            modalVisible &&
                                            addNumberForm.getFieldValue(
                                                'organization'
                                            )
                                        }
                                    />
                                    <span className="tip">
                                        邀请成员加入的组织
                                    </span>
                                </Item>
                                <Item label="分配部门">
                                    <Input
                                        placeholder=""
                                        disabled
                                        value={
                                            modalVisible &&
                                            addNumberForm.getFieldValue(
                                                'department'
                                            )
                                        }
                                    />
                                    <span className="tip">
                                        邀请成员加入的部门
                                    </span>
                                </Item>
                            </>
                        )}

                        <Item
                            validateStatus={''}
                            rules={[{ required: true }]}
                            label={intl.get('projectSpace.selectRole')}>
                            <Select
                                value={selectRole}
                                placeholder="请选择角色"
                                onChange={onRoleChange}>
                                {roleList
                                    .filter(v => v.id !== -1)
                                    .map(v => (
                                        <Select.Option value={v.id} key={v.id}>
                                            {v.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Item>
                        {selectRole && <>{SHARE_MODEL}</>}
                    </section>
                </Form>
            </BaseModal>

            <Modal
                title="操作离职"
                visible={isOpen}
                className="quitted-modal"
                closeIcon={<i className="close-btn icon-close1 outlined" />}
                onOk={onSubmitQuitted}
                onCancel={onClose}
                centered
                okButtonProps={{
                    loading,
                }}
                width="37.5rem"
                okText="确定离职"
                cancelText="取消">
                <div className="user-header">
                    <Avatar size={48} src={quittedCurrent.head_url}>
                        {quittedCurrent.username?.[0]}
                    </Avatar>
                    {quittedCurrent.username}
                </div>
                <div className="table-title">移交该成员的资源</div>
                <Form
                    form={handOverRef}
                    component={false}
                    onFieldsChange={onFieldsChange}>
                    <Table
                        className="quitted-table"
                        loading={isLoading}
                        columns={columnsQuitted}
                        dataSource={quittedRows}
                        size="small"
                        pagination={false}
                        rowKey="id"
                        bordered
                    />
                </Form>
            </Modal>
        </div>
    );
}
